import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, TrendingUp, DollarSign, Gift, ShoppingBag, Trophy, ShieldAlert, Snowflake, Coins, Search, X, Download, CheckSquare, Square, Mail, Send } from 'lucide-react';
import AdminOpsPanel from '@/components/AdminOpsPanel';
import AdminActivityFeed from '@/components/AdminActivityFeed';

function Stat({ icon: Icon, label, value, sub, color = '#FCD535' }) {
  return (
    <div className="border border-[#2B3139] bg-[#181A20] p-4">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4" style={{ color }} /><div className="text-[10px] uppercase font-mono text-[#B7BDC6]">{label}</div></div>
      <div className="text-white text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-[10px] text-[#5E6673] mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [adjustForm, setAdjustForm] = useState({ user_id: '', symbol: 'USDT', delta: '', reason: '' });
  const [freezeForm, setFreezeForm] = useState({ user_id: '', frozen: true, reason: '' });
  const [grantPtsForm, setGrantPtsForm] = useState({ user_id: '', points: '', reason: '' });
  const [grantPerkForm, setGrantPerkForm] = useState({ user_id: '', reward_id: 'nft_booster' });
  const [audit, setAudit] = useState({ trades: [], marketplace: [], actions: [], batches: [] });
  const [cancelIds, setCancelIds] = useState({ sponsorship: '', launchpad: '', listing: '' });
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  const isAdmin = user && (user.role === 'admin' || user.email === 'admin@cryptox.com' || user.email === 'admin@bitgonode.com');

  const load = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get('/admin/dashboard/stats');
      setStats(r.data);
    } catch (e) { toast.error('Failed to load stats'); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [isAdmin]);

  const loadAudit = async () => {
    try {
      const [t, m, a, b] = await Promise.all([
        api.get('/admin/audit/trades?limit=30'),
        api.get('/admin/audit/marketplace?limit=30'),
        api.get('/admin/audit/actions?limit=30'),
        api.get('/admin/audit/batches?limit=50'),
      ]);
      setAudit({
        trades: t.data.trades || [],
        marketplace: m.data.listings || [],
        actions: a.data.actions || [],
        batches: b.data.batches || [],
      });
    } catch { /* silent */ }
  };
  useEffect(() => { if (tab === 'audit' && isAdmin) loadAudit(); }, [tab, isAdmin]);

  const undoBatch = async (batch) => {
    const label = `${batch.action.toUpperCase()} on ${batch.succeeded_count} user(s)${batch.symbol ? ` (${batch.amount} ${batch.symbol})` : batch.amount ? ` (${batch.amount} pts)` : ''}`;
    if (!window.confirm(`REVERSE this batch?\n\n${label}\n\nThis will apply the opposite action (mint↔burn, freeze↔unfreeze, points deducted).`)) return;
    try {
      const r = await api.post(`/admin/audit/batch/${batch.bulk_batch_id}/undo`);
      toast.success(`Reversed: ${r.data.reverse_action} on ${r.data.succeeded_count} user(s)`);
      loadAudit();
      if (tab === 'users') runSearch(searchQ);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  const runSearch = async (q) => {
    if (!isAdmin) return;
    setSearching(true);
    try {
      const r = await api.get(`/admin/users/search?q=${encodeURIComponent(q || '')}&limit=25`);
      setSearchResults(r.data.users || []);
    } catch (e) { toast.error('Search failed'); }
    finally { setSearching(false); }
  };
  useEffect(() => {
    if (tab !== 'users' || !isAdmin) return;
    const t = setTimeout(() => runSearch(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, tab, isAdmin]);

  const pickUser = (u) => {
    setSelectedUser(u);
    setAdjustForm(f => ({ ...f, user_id: u.user_id }));
    setFreezeForm(f => ({ ...f, user_id: u.user_id, frozen: !u.frozen }));
    setGrantPtsForm(f => ({ ...f, user_id: u.user_id }));
    setGrantPerkForm(f => ({ ...f, user_id: u.user_id }));
    toast.success(`Selected ${u.email}`);
  };

  const toggleCheck = (uid) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const allChecked = searchResults.length > 0 && searchResults.every(u => checkedIds.has(u.user_id));
  const someChecked = checkedIds.size > 0 && !allChecked;
  const toggleAll = () => {
    if (allChecked) setCheckedIds(new Set());
    else setCheckedIds(new Set(searchResults.map(u => u.user_id)));
  };

  const exportCsv = () => {
    const rows = searchResults;
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    const headers = ['user_id', 'email', 'name', 'role', 'kyc_status', 'frozen', 'usdt_balance', 'referral_points', 'asset_count', 'active_perks', 'referral_code', 'created_at'];
    const esc = v => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(',')].concat(rows.map(r => headers.map(h => esc(r[h])).join(',')));
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bitgonode_users_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const runBulk = async (action, opts = {}) => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) { toast.error('No users selected'); return; }
    const destructive = action === 'burn' || action === 'freeze';
    if (destructive && ids.length >= 5) {
      const label = action === 'burn' ? `BURN ${opts.amount} ${opts.symbol || 'USDT'} from` : 'FREEZE trading for';
      if (!window.confirm(`${label} ${ids.length} users? This cannot be undone in bulk.`)) return;
    }
    setBulkRunning(true);
    try {
      const body = { action, user_ids: ids, reason: opts.reason || `admin bulk ${action}` };
      if (action === 'mint' || action === 'burn') { body.symbol = opts.symbol || 'USDT'; body.amount = opts.amount; }
      if (action === 'grant_points') { body.amount = opts.amount; }
      const r = await api.post('/admin/users/bulk_action', body);
      const { succeeded_count, failed_count } = r.data;
      toast.success(`${action}: ${succeeded_count} ok${failed_count ? `, ${failed_count} failed` : ''}`);
      setCheckedIds(new Set());
      runSearch(searchQ);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const promptAmount = (label, dflt) => {
    const v = window.prompt(label, dflt);
    if (v === null) return null;
    const n = parseFloat(v);
    if (!isFinite(n) || n <= 0) { toast.error('Invalid amount'); return null; }
    return n;
  };

  const runDigest = async (dry_run) => {
    if (!dry_run && !window.confirm('Send weekly digest emails to all eligible users NOW?')) return;
    setDigestBusy(true);
    setDigestPreview(null);
    try {
      const r = await api.post('/admin/digest/run', { dry_run, force: dry_run });
      setDigestPreview(r.data);
      toast.success(dry_run ? `Dry run: ${r.data.sent} would be sent` : `Sent ${r.data.sent} digests`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest run failed');
    } finally {
      setDigestBusy(false);
    }
  };

  const submit = async (path, body, msg) => {
    try {
      await api.post(path, body);
      toast.success(msg || 'Done');
      load();
      if (tab === 'users') runSearch(searchQ);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!isAdmin) return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="admin-forbidden">
      <div className="max-w-md w-full border border-[#F6465D]/30 bg-[#181A20] p-8 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-[#F6465D]" />
        <div className="text-white text-xl font-bold mb-2">Admin access required</div>
        <div className="text-[#B7BDC6] text-sm mb-5">
          {user ? 'Your account does not have admin privileges. Please contact the exchange operator.'
                : 'You need to sign in with an admin account to view this page.'}
        </div>
        {!user && (
          <a href="/login" className="inline-block bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm px-5 py-2.5 text-sm uppercase" data-testid="admin-guard-login-btn">
            Log in as admin
          </a>
        )}
        {!user && (
          <div className="mt-4 text-[10px] font-mono text-[#5E6673]">
            admin@cryptox.com · seeded on startup
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E11] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-4xl font-bold mb-1">Admin Ops</h1>
        <p className="text-[#B7BDC6] text-sm mb-6">Full control over BITGONODE — updates every 30s</p>

        <div className="inline-flex border border-[#2B3139] p-0.5 bg-[#181A20] mb-6" data-testid="admin-tabs">
          {[['overview', 'Overview'], ['users', 'Users Search'], ['perks', 'Actions'], ['ops', 'Ops Queue'], ['cancels', 'Force-cancel'], ['audit', 'Audit']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 text-xs uppercase font-mono ${tab === k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6]'}`} data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === 'overview' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Users} label="Total users" value={stats.users.total} sub={`+${stats.users.new_24h} in 24h · ${stats.users.frozen} frozen`} />
            <Stat icon={TrendingUp} label="24h Volume" value={`$${stats.trading_24h.volume_usd.toLocaleString(undefined, {maximumFractionDigits:0})}`} sub={`${stats.trading_24h.count} trades`} color="#0ECB81" />
            <Stat icon={DollarSign} label="24h Fees" value={`$${stats.trading_24h.fees_earned_usd.toFixed(2)}`} sub="Trading fees earned" color="#0ECB81" />
            <Stat icon={Gift} label="Active Perks" value={stats.perks.active} sub="Unconsumed + unexpired" color="#B26BFF" />
            <Stat icon={ShoppingBag} label="Marketplace Vol 7d" value={`${stats.marketplace.volume_7d_pts.toLocaleString(undefined, {maximumFractionDigits:0})} pts`} sub={`${stats.marketplace.sold_7d} sold · ${stats.marketplace.fees_burned_7d_pts.toFixed(0)} burned`} color="#B26BFF" />
            <Stat icon={Trophy} label="Sponsorship Rev 7d" value={`${stats.sponsorship_revenue_7d.usdt.toLocaleString()} USDT`} sub={`${stats.sponsorship_revenue_7d.count} consumed`} color="#F6465D" />
            <Stat icon={ShieldAlert} label="KYC Pending" value={stats.kyc.pending} sub="Awaiting admin review" color="#F6465D" />
            <Stat icon={TrendingUp} label="Launchpad Live" value={stats.launchpad.live} sub={`${stats.launchpad.pending_approval} pending approval`} />
            <div className="col-span-2 md:col-span-4">
              <AdminActivityFeed />
            </div>
            <div className="col-span-2 md:col-span-4 border border-[#2B3139] bg-[#181A20] p-4">
              <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">Top Referrers This Month</div>
              {stats.top_referrers_month.length === 0 ? (
                <div className="text-sm text-[#5E6673]">No referrals this month yet</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {stats.top_referrers_month.map((t, i) => (
                    <div key={t._id} className="flex items-center gap-3 text-sm">
                      <span className="text-[#FCD535] font-mono w-6">#{i+1}</span>
                      <span className="text-white flex-1 truncate">{t.name}</span>
                      <span className="text-[#848E9C] text-xs truncate">{t.email}</span>
                      <span className="text-[#0ECB81] font-mono">{t.count} refs</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2 md:col-span-4 border border-[#2B3139] bg-[#181A20] p-4" data-testid="admin-digest-card">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-[#FCD535]" />
                <div className="flex-1">
                  <div className="text-white font-semibold text-sm">Weekly Digest Emails</div>
                  <div className="text-[10px] font-mono text-[#5E6673]">Auto-scheduled every Sunday 09:00 UTC — trigger manually here</div>
                </div>
                <Button onClick={() => runDigest(true)} disabled={digestBusy} variant="outline" size="sm" className="border-[#2B3139] bg-[#0B0E11] text-white hover:bg-[#2B3139] rounded-sm text-xs" data-testid="digest-dry-run-btn">
                  {digestBusy ? '…' : 'Preview (dry-run)'}
                </Button>
                <Button onClick={() => runDigest(false)} disabled={digestBusy} size="sm" className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm text-xs font-semibold" data-testid="digest-send-now-btn">
                  <Send className="w-3 h-3 mr-1" /> Send Now
                </Button>
              </div>
              {digestPreview && (
                <div className="text-[11px] font-mono text-[#B7BDC6] space-y-1" data-testid="digest-preview-result">
                  <div>
                    <span className="text-[#FCD535]">{digestPreview.sent}</span> {digestPreview.dry_run ? 'would send' : 'sent'} ·
                    <span className="text-[#848E9C] ml-1">{digestPreview.skipped_optout} opted-out</span> ·
                    <span className="text-[#848E9C] ml-1">{digestPreview.skipped_inactive_or_empty} inactive/empty</span> ·
                    <span className="text-[#848E9C] ml-1">{digestPreview.skipped_already_sent} already-sent</span>
                    <span className="text-[#5E6673] ml-1">({digestPreview.week_key})</span>
                  </div>
                  {digestPreview.previews?.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-[#2B3139] p-2 bg-[#0B0E11]">
                      {digestPreview.previews.slice(0, 20).map((p, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px]">
                          <span className="text-[#B7BDC6] flex-1 truncate">{p.email}</span>
                          <span className="text-[#FCD535]">${p.portfolio_usd?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                          <span className={p.delta_7d >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>{p.delta_7d >= 0 ? '+' : ''}{p.delta_7d}%</span>
                          <span className="text-[#B26BFF]">{p.battles}</span>
                        </div>
                      ))}
                      {digestPreview.previews.length > 20 && <div className="text-[10px] text-[#5E6673] mt-1">+ {digestPreview.previews.length - 20} more</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-users-search">
            {/* Left: search + results */}
            <div className="lg:col-span-2 border border-[#2B3139] bg-[#181A20] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-[#FCD535]" />
                <span className="font-semibold">Search users</span>
                <span className="text-[10px] font-mono text-[#5E6673]">by email, name, user_id or ref code</span>
              </div>
              <div className="relative mb-3">
                <Input
                  placeholder="Type to search…"
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  className="bg-[#0B0E11] border-[#2B3139] text-white pr-8"
                  data-testid="admin-user-search-input"
                />
                {searchQ && (
                  <button onClick={() => setSearchQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#848E9C] hover:text-white" data-testid="admin-user-search-clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-[10px] uppercase font-mono text-[#5E6673]">
                  {searching ? 'Searching…' : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'}`}
                  {checkedIds.size > 0 && <span className="ml-2 text-[#FCD535]">· {checkedIds.size} checked</span>}
                </div>
                <div className="flex items-center gap-2">
                  {searchResults.length > 0 && (
                    <button
                      onClick={toggleAll}
                      className="flex items-center gap-1 text-[10px] uppercase font-mono text-[#B7BDC6] hover:text-white"
                      data-testid="admin-user-select-all"
                    >
                      {allChecked ? <CheckSquare className="w-3.5 h-3.5 text-[#FCD535]" /> : someChecked ? <CheckSquare className="w-3.5 h-3.5 text-[#B7BDC6]" /> : <Square className="w-3.5 h-3.5" />}
                      {allChecked ? 'Unselect all' : 'Select all'}
                    </button>
                  )}
                  <button
                    onClick={exportCsv}
                    disabled={searchResults.length === 0}
                    className="flex items-center gap-1 text-[10px] uppercase font-mono text-[#0ECB81] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="admin-user-export-csv"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </button>
                </div>
              </div>

              {checkedIds.size > 0 && (
                <div className="border border-[#FCD535]/40 bg-[#FCD535]/5 p-2 mb-2 flex flex-wrap items-center gap-1.5" data-testid="admin-bulk-toolbar">
                  <span className="text-[10px] uppercase font-mono text-[#FCD535] mr-1">Bulk · {checkedIds.size}</span>
                  <Button size="sm" disabled={bulkRunning} onClick={() => runBulk('mint', { amount: 1000, symbol: 'USDT' })} className="h-6 px-2 text-[10px] bg-[#0ECB81] hover:bg-[#0BA36B] text-white" data-testid="bulk-mint-1k">+1k USDT</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => { const a = promptAmount('Mint USDT to each user:', '5000'); if (a) runBulk('mint', { amount: a, symbol: 'USDT' }); }} className="h-6 px-2 text-[10px] bg-[#0ECB81] hover:bg-[#0BA36B] text-white" data-testid="bulk-mint-custom">Mint…</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => { const a = promptAmount('Burn USDT from each user:', '1000'); if (a) runBulk('burn', { amount: a, symbol: 'USDT' }); }} className="h-6 px-2 text-[10px] bg-[#F6465D] hover:bg-[#D93B4E] text-white" data-testid="bulk-burn-custom">Burn…</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => runBulk('freeze')} className="h-6 px-2 text-[10px] bg-[#F6465D] hover:bg-[#D93B4E] text-white" data-testid="bulk-freeze">Freeze</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => runBulk('unfreeze')} className="h-6 px-2 text-[10px] bg-[#5E6673] hover:bg-[#848E9C] text-white" data-testid="bulk-unfreeze">Unfreeze</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => runBulk('grant_points', { amount: 100 })} className="h-6 px-2 text-[10px] bg-[#B26BFF] hover:bg-[#9B4EE0] text-white" data-testid="bulk-pts-100">+100 pts</Button>
                  <Button size="sm" disabled={bulkRunning} onClick={() => { const a = promptAmount('Grant points to each user:', '500'); if (a) runBulk('grant_points', { amount: a }); }} className="h-6 px-2 text-[10px] bg-[#B26BFF] hover:bg-[#9B4EE0] text-white" data-testid="bulk-pts-custom">Points…</Button>
                  <button onClick={() => setCheckedIds(new Set())} className="ml-auto text-[10px] font-mono text-[#B7BDC6] hover:text-white underline" data-testid="bulk-clear">Clear</button>
                </div>
              )}
              <div className="max-h-[520px] overflow-y-auto flex flex-col gap-1" data-testid="admin-user-search-results">
                {searchResults.length === 0 && !searching && (
                  <div className="text-sm text-[#5E6673] py-4 text-center">No users found</div>
                )}
                {searchResults.map(u => {
                  const isSel = selectedUser?.user_id === u.user_id;
                  const isChecked = checkedIds.has(u.user_id);
                  return (
                    <div
                      key={u.user_id}
                      className={`flex items-stretch border transition ${isSel ? 'border-[#FCD535] bg-[#FCD535]/10' : isChecked ? 'border-[#FCD535]/50 bg-[#FCD535]/5' : 'border-[#2B3139] bg-[#0B0E11] hover:border-[#5E6673]'}`}
                    >
                      <button
                        onClick={() => toggleCheck(u.user_id)}
                        className="px-2 flex items-center border-r border-[#2B3139] hover:bg-[#FCD535]/10"
                        data-testid={`admin-user-check-${u.user_id}`}
                        aria-label={isChecked ? 'Uncheck' : 'Check'}
                      >
                        {isChecked ? <CheckSquare className="w-4 h-4 text-[#FCD535]" /> : <Square className="w-4 h-4 text-[#5E6673]" />}
                      </button>
                      <button
                        onClick={() => pickUser(u)}
                        className="flex-1 text-left p-2 min-w-0"
                        data-testid={`admin-user-row-${u.user_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm font-semibold truncate">{u.name || '(no name)'}</span>
                              {u.role === 'admin' && <span className="text-[9px] px-1 bg-[#FCD535] text-[#0B0E11] font-mono">ADMIN</span>}
                              {u.frozen && <span className="text-[9px] px-1 bg-[#F6465D] text-white font-mono">FROZEN</span>}
                              {u.kyc_status === 'verified' && <span className="text-[9px] px-1 bg-[#0ECB81] text-white font-mono">KYC</span>}
                            </div>
                            <div className="text-[11px] text-[#B7BDC6] truncate font-mono">{u.email}</div>
                            <div className="text-[10px] text-[#5E6673] truncate font-mono">{u.user_id}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[#FCD535] font-mono text-sm">${u.usdt_balance?.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                            <div className="text-[10px] text-[#848E9C] font-mono">{u.asset_count} assets · {u.active_perks} perks</div>
                            <div className="text-[10px] text-[#B26BFF] font-mono">{u.referral_points} pts</div>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: quick actions for selected user */}
            <div className="border border-[#2B3139] bg-[#181A20] p-4" data-testid="admin-user-actions">
              {!selectedUser ? (
                <div className="text-center text-[#5E6673] text-sm py-16">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Pick a user on the left to run quick actions
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="pb-3 border-b border-[#2B3139]">
                    <div className="text-white font-semibold truncate">{selectedUser.name || '(no name)'}</div>
                    <div className="text-[11px] font-mono text-[#B7BDC6] truncate">{selectedUser.email}</div>
                    <div className="text-[10px] font-mono text-[#5E6673] truncate">{selectedUser.user_id}</div>
                    <div className="text-sm text-[#FCD535] font-mono mt-1">${selectedUser.usdt_balance?.toLocaleString()} USDT · {selectedUser.referral_points} pts</div>
                  </div>

                  {/* Quick mint / burn */}
                  <div>
                    <div className="text-[10px] uppercase font-mono text-[#B7BDC6] mb-1">Quick mint / burn USDT</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/wallet_adjust`, {symbol: 'USDT', delta: 1000, reason: 'admin quick mint'}, '+1,000 USDT minted')} className="bg-[#0ECB81] hover:bg-[#0BA36B] text-white text-xs" data-testid="quick-mint-1k">+1,000</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/wallet_adjust`, {symbol: 'USDT', delta: 10000, reason: 'admin quick mint'}, '+10,000 USDT minted')} className="bg-[#0ECB81] hover:bg-[#0BA36B] text-white text-xs" data-testid="quick-mint-10k">+10,000</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/wallet_adjust`, {symbol: 'USDT', delta: -1000, reason: 'admin quick burn'}, '-1,000 USDT burned')} className="bg-[#F6465D] hover:bg-[#D93B4E] text-white text-xs" data-testid="quick-burn-1k">-1,000</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/wallet_adjust`, {symbol: 'USDT', delta: -selectedUser.usdt_balance, reason: 'admin drain'}, 'Balance drained')} className="bg-[#F6465D] hover:bg-[#D93B4E] text-white text-xs" data-testid="quick-drain">Drain all</Button>
                    </div>
                  </div>

                  {/* Freeze / unfreeze */}
                  <div>
                    <div className="text-[10px] uppercase font-mono text-[#B7BDC6] mb-1">Account status</div>
                    {selectedUser.frozen ? (
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/freeze`, {frozen: false, reason: 'admin unfreeze'}, 'User unfrozen')} className="w-full bg-[#0ECB81] hover:bg-[#0BA36B] text-white text-xs" data-testid="quick-unfreeze">Unfreeze</Button>
                    ) : (
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/freeze`, {frozen: true, reason: 'admin freeze'}, 'User frozen')} className="w-full bg-[#F6465D] hover:bg-[#D93B4E] text-white text-xs" data-testid="quick-freeze">Freeze trades</Button>
                    )}
                  </div>

                  {/* Grant points */}
                  <div>
                    <div className="text-[10px] uppercase font-mono text-[#B7BDC6] mb-1">Grant referral points</div>
                    <div className="grid grid-cols-3 gap-2">
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_points`, {points: 100, reason: 'admin grant'}, '+100 pts')} className="bg-[#B26BFF] hover:bg-[#9B4EE0] text-white text-xs" data-testid="quick-pts-100">+100</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_points`, {points: 500, reason: 'admin grant'}, '+500 pts')} className="bg-[#B26BFF] hover:bg-[#9B4EE0] text-white text-xs" data-testid="quick-pts-500">+500</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_points`, {points: 2000, reason: 'admin grant'}, '+2,000 pts')} className="bg-[#B26BFF] hover:bg-[#9B4EE0] text-white text-xs" data-testid="quick-pts-2k">+2,000</Button>
                    </div>
                  </div>

                  {/* Grant perk */}
                  <div>
                    <div className="text-[10px] uppercase font-mono text-[#B7BDC6] mb-1">Grant perk</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_perk`, {reward_id: 'fee_discount_50'}, 'Fee discount 50% granted')} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] text-xs" data-testid="quick-perk-fee50">Fee -50%</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_perk`, {reward_id: 'fee_discount_100'}, 'Zero fees granted')} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] text-xs" data-testid="quick-perk-fee100">Zero fees</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_perk`, {reward_id: 'nft_booster'}, 'NFT booster granted')} className="bg-[#B26BFF] hover:bg-[#9B4EE0] text-white text-xs" data-testid="quick-perk-nft">NFT Booster</Button>
                      <Button onClick={() => submit(`/admin/users/${selectedUser.user_id}/grant_perk`, {reward_id: 'battle_insurance'}, 'Battle insurance granted')} className="bg-[#0ECB81] hover:bg-[#0BA36B] text-white text-xs" data-testid="quick-perk-battle">Battle Ins.</Button>
                    </div>
                  </div>

                  <div className="text-[10px] text-[#5E6673] pt-2 border-t border-[#2B3139]">
                    Need custom values? Use the <button onClick={() => setTab('perks')} className="text-[#FCD535] underline" data-testid="go-to-perks-tab">Actions tab</button> — the user_id is already filled in.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'perks' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="flex items-center gap-2 mb-3"><Coins className="w-4 h-4 text-[#FCD535]" /><span className="font-semibold">Wallet Mint / Burn</span></div>
              <Input placeholder="user_id" value={adjustForm.user_id} onChange={e=>setAdjustForm({...adjustForm, user_id: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="adjust-user-id" />
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Input placeholder="Symbol (USDT)" value={adjustForm.symbol} onChange={e=>setAdjustForm({...adjustForm, symbol: e.target.value})} className="bg-[#0B0E11] border-[#2B3139] text-white" data-testid="adjust-symbol" />
                <Input type="number" placeholder="Delta (+/-)" value={adjustForm.delta} onChange={e=>setAdjustForm({...adjustForm, delta: e.target.value})} className="bg-[#0B0E11] border-[#2B3139] text-white font-mono" data-testid="adjust-delta" />
              </div>
              <Input placeholder="Reason" value={adjustForm.reason} onChange={e=>setAdjustForm({...adjustForm, reason: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="adjust-reason" />
              <Button onClick={() => submit(`/admin/users/${adjustForm.user_id}/wallet_adjust`, {symbol: adjustForm.symbol, delta: Number(adjustForm.delta), reason: adjustForm.reason}, 'Wallet adjusted')} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11]" data-testid="adjust-submit">Apply</Button>
            </div>

            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="flex items-center gap-2 mb-3"><Snowflake className="w-4 h-4 text-[#F6465D]" /><span className="font-semibold">Freeze / Unfreeze</span></div>
              <Input placeholder="user_id" value={freezeForm.user_id} onChange={e=>setFreezeForm({...freezeForm, user_id: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="freeze-user-id" />
              <div className="flex gap-2 mb-2">
                <label className="text-xs flex items-center gap-1 text-[#B7BDC6]"><input type="radio" checked={freezeForm.frozen} onChange={()=>setFreezeForm({...freezeForm, frozen: true})} /> Freeze</label>
                <label className="text-xs flex items-center gap-1 text-[#B7BDC6]"><input type="radio" checked={!freezeForm.frozen} onChange={()=>setFreezeForm({...freezeForm, frozen: false})} /> Unfreeze</label>
              </div>
              <Input placeholder="Reason" value={freezeForm.reason} onChange={e=>setFreezeForm({...freezeForm, reason: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="freeze-reason" />
              <Button onClick={() => submit(`/admin/users/${freezeForm.user_id}/freeze`, {frozen: freezeForm.frozen, reason: freezeForm.reason}, freezeForm.frozen ? 'User frozen' : 'User unfrozen')} className="w-full bg-[#F6465D] hover:bg-[#D93B4E] text-white" data-testid="freeze-submit">Apply</Button>
            </div>

            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-[#B26BFF]" /><span className="font-semibold">Grant Points</span></div>
              <Input placeholder="user_id" value={grantPtsForm.user_id} onChange={e=>setGrantPtsForm({...grantPtsForm, user_id: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="grant-pts-user-id" />
              <Input type="number" placeholder="Points" value={grantPtsForm.points} onChange={e=>setGrantPtsForm({...grantPtsForm, points: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white font-mono" data-testid="grant-pts-amount" />
              <Input placeholder="Reason" value={grantPtsForm.reason} onChange={e=>setGrantPtsForm({...grantPtsForm, reason: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="grant-pts-reason" />
              <Button onClick={() => submit(`/admin/users/${grantPtsForm.user_id}/grant_points`, {points: Number(grantPtsForm.points), reason: grantPtsForm.reason}, 'Points granted')} className="w-full bg-[#B26BFF] hover:bg-[#9B4EE0] text-white" data-testid="grant-pts-submit">Grant</Button>
            </div>

            <div className="border border-[#2B3139] bg-[#181A20] p-4">
              <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-[#0ECB81]" /><span className="font-semibold">Grant Perk</span></div>
              <Input placeholder="user_id" value={grantPerkForm.user_id} onChange={e=>setGrantPerkForm({...grantPerkForm, user_id: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid="grant-perk-user-id" />
              <select value={grantPerkForm.reward_id} onChange={e=>setGrantPerkForm({...grantPerkForm, reward_id: e.target.value})} className="w-full h-10 px-3 mb-2 bg-[#0B0E11] border border-[#2B3139] text-white text-sm" data-testid="grant-perk-reward">
                <option value="fee_discount_50">Fee Discount 50%</option>
                <option value="fee_discount_100">Zero Fees</option>
                <option value="nft_booster">NFT Rarity Booster</option>
                <option value="battle_insurance">Battle Insurance</option>
              </select>
              <Button onClick={() => submit(`/admin/users/${grantPerkForm.user_id}/grant_perk`, {reward_id: grantPerkForm.reward_id}, 'Perk granted')} className="w-full bg-[#0ECB81] hover:bg-[#0BA36B] text-white" data-testid="grant-perk-submit">Grant</Button>
            </div>
          </div>
        )}

        {tab === 'ops' && <AdminOpsPanel />}

        {tab === 'cancels' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[['sponsorship', 'Sponsorship', 'sponsorship_id', `/admin/sponsorships/${cancelIds.sponsorship}/force_cancel`],
              ['launchpad', 'Launchpad Project', 'project_id', `/admin/launchpad/projects/${cancelIds.launchpad}/force_cancel`],
              ['listing', 'Marketplace Listing', 'listing_id', `/admin/marketplace/${cancelIds.listing}/force_cancel`]].map(([k, label, ph, path]) => (
              <div key={k} className="border border-[#F6465D]/30 bg-[#F6465D]/5 p-4">
                <div className="font-semibold mb-2">Cancel {label}</div>
                <Input placeholder={ph} value={cancelIds[k]} onChange={e=>setCancelIds({...cancelIds, [k]: e.target.value})} className="mb-2 bg-[#0B0E11] border-[#2B3139] text-white" data-testid={`cancel-${k}-input`} />
                <Button onClick={() => submit(path, {}, `${label} cancelled + refunded`)} className="w-full bg-[#F6465D] hover:bg-[#D93B4E] text-white" data-testid={`cancel-${k}-submit`}>Force-cancel + Refund</Button>
              </div>
            ))}
          </div>
        )}

        {tab === 'audit' && (
          <div>
            {/* Bulk Action Batches — with undo */}
            <div className="border border-[#2B3139] bg-[#181A20] p-3 mb-4" data-testid="audit-batches-panel">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs uppercase font-mono text-[#B7BDC6]">Bulk Action Batches · {audit.batches.length}</div>
                <div className="text-[10px] font-mono text-[#5E6673]">Undo any promo in one click</div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {audit.batches.length === 0 && <div className="text-[#5E6673] text-xs py-4 text-center">No bulk batches yet — run one from Users Search</div>}
                {audit.batches.length > 0 && (
                  <table className="w-full text-[11px] font-mono">
                    <thead className="text-[#848E9C] text-[10px] uppercase">
                      <tr className="border-b border-[#2B3139]">
                        <th className="text-left py-1.5 pr-2">Batch</th>
                        <th className="text-left py-1.5 pr-2">Action</th>
                        <th className="text-right py-1.5 pr-2">Amount</th>
                        <th className="text-right py-1.5 pr-2">Users</th>
                        <th className="text-left py-1.5 pr-2">Admin</th>
                        <th className="text-left py-1.5 pr-2">When</th>
                        <th className="text-right py-1.5">Undo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.batches.map(b => {
                        const isUndo = String(b.kind || '').startsWith('bulk_undo_');
                        return (
                          <tr key={b.bulk_batch_id} className="border-b border-[#2B3139]/50 hover:bg-[#0B0E11]" data-testid={`batch-row-${b.bulk_batch_id}`}>
                            <td className="py-1.5 pr-2 text-[#B7BDC6] truncate max-w-[110px]" title={b.bulk_batch_id}>{b.bulk_batch_id}</td>
                            <td className={`py-1.5 pr-2 uppercase ${isUndo ? 'text-[#B26BFF]' : 'text-white'}`}>{b.action || b.kind}</td>
                            <td className="py-1.5 pr-2 text-right text-[#FCD535]">
                              {b.amount != null ? `${b.amount}${b.symbol ? ' ' + b.symbol : ' pts'}` : '—'}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-white">
                              {b.succeeded_count}{b.failed_count > 0 && <span className="text-[#F6465D]"> /{b.failed_count}✗</span>}
                            </td>
                            <td className="py-1.5 pr-2 text-[#848E9C] truncate max-w-[150px]" title={b.admin_email}>{b.admin_email || b.admin_id?.slice(-8)}</td>
                            <td className="py-1.5 pr-2 text-[#5E6673] whitespace-nowrap">{new Date(b.at).toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="py-1.5 text-right">
                              {b.reversed_at ? (
                                <span className="text-[10px] font-mono text-[#B26BFF]" data-testid={`batch-reversed-${b.bulk_batch_id}`}>reversed</span>
                              ) : b.reversible ? (
                                <button
                                  onClick={() => undoBatch(b)}
                                  className="text-[10px] font-mono px-2 py-0.5 border border-[#F6465D]/40 text-[#F6465D] hover:bg-[#F6465D]/10"
                                  data-testid={`batch-undo-${b.bulk_batch_id}`}
                                >
                                  UNDO
                                </button>
                              ) : (
                                <span className="text-[10px] font-mono text-[#5E6673]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {[['trades', 'Recent Trades', audit.trades, t => `${t.side.toUpperCase()} ${t.amount?.toFixed?.(4) || t.amount} ${t.symbol} @ $${t.price} · fee $${t.fee_usd?.toFixed?.(4) || '0'}`],
                ['marketplace', 'Recent Listings', audit.marketplace, l => `${l.status.toUpperCase()} ${l.perk_name} @ ${l.ask_points}p`],
                ['actions', 'Admin Actions', audit.actions, a => `${a.kind} · admin=${a.admin_id?.slice?.(-6)} · ${JSON.stringify(a).slice(0,80)}`]].map(([k, label, items, fmt]) => (
                <div key={k} className="border border-[#2B3139] bg-[#181A20] p-3">
                  <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">{label} · {items.length}</div>
                  <div className="max-h-96 overflow-y-auto flex flex-col gap-1 text-[11px] font-mono">
                    {items.length === 0 && <div className="text-[#5E6673]">None yet</div>}
                    {items.map((it, i) => (
                      <div key={i} className="text-[#B7BDC6] border-l-2 border-[#2B3139] pl-2 py-1">
                        <div className="text-white truncate">{fmt(it)}</div>
                        <div className="text-[#5E6673]">{new Date(it.at || it.created_at || it.listed_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
